/**
 * 用户级隔离存储（按 userId 前缀区分）
 *
 * 背景：scopedStorage 只按 appId 做前缀隔离，同一 App 下不同账号共享同一份数据。
 * 业务数据（用户资料/训练记录/体重/饮食/会员等）必须按账号独立存储，
 * 因此在 scopedStorage 之上再包一层 userId 前缀。
 *
 * 存储键结构：
 *   scopedStorage 内部：<appId>:u_<userId>:<业务key>
 *
 * 使用规则：
 *   - 用户业务数据（profile / training / weight / diet / member / plan 等）→ useUserStorage() / getUserStorage()
 *   - 训练会话、照片与偏好同样按用户隔离；部署适配层负责云端同步
 */

import { getCurrentUserProfile, scopedStorage } from '@lark-apaas/client-toolkit-lite';

/**
 * 获取当前用户 ID，未登录时用 'anon' 兜底（保证存储不报错）。
 * 注意：'anon' 是所有未登录用户共享的命名空间，登录后会切换到真实 userId。
 */
export function getCurrentUserId(): string {
  try {
    const user = getCurrentUserProfile();
    const id = user?.user_id;
    if (id && typeof id === 'string' && id.length > 0 && id !== 'undefined' && id !== 'null') {
      return id;
    }
    return 'anon';
  } catch {
    return 'anon';
  }
}

/** 拼接带用户前缀的存储 key */
function userKey(key: string, userId: string): string {
  return `u_${userId}:${key}`;
}

/**
 * 用户级 scopedStorage 封装（函数式，可在组件外 / hooks 内调用）。
 * 每次读写都会从 SDK 取最新 userId，保证切换账号后自动落到新账号的命名空间。
 */
export const userStorage = {
  getItem(key: string): string | null {
    const userId = getCurrentUserId();
    return scopedStorage.getItem(userKey(key, userId));
  },
  setItem(key: string, value: string): void {
    const userId = getCurrentUserId();
    scopedStorage.setItem(userKey(key, userId), value);
  },
  removeItem(key: string): void {
    const userId = getCurrentUserId();
    scopedStorage.removeItem(userKey(key, userId));
  },
  /** 读取当前用户名下指定前缀的所有 key（用于迁移 / 清理） */
  keys(prefix: string): string[] {
    const userId = getCurrentUserId();
    const fullPrefix = userKey(prefix, userId);
    const result: string[] = [];
    for (let i = 0; i < scopedStorage.length; i++) {
      const k = scopedStorage.key(i);
      if (k?.startsWith(fullPrefix)) result.push(k.slice(fullPrefix.length));
    }
    return result;
  },
};

export default userStorage;
